import React, { useContext } from 'react'
import DOMPurify from 'dompurify'
import { ErrorBoundary, TextContext } from 'hocs'

// component stack
import * as Styled from './Detail.styles'
import { AnchorText, PrintButton, SubHeading } from 'components/atoms'

import {
  Accordion,
  DetailHeader,
  GeneralInformation,
  LinkList,
  MessageBox,
  Overlay,
  ReadMoreTeaser,
} from 'components/molecules'

// helpers
import { filterTreatments } from '../../helpers/helpers'
import {
  DetailContentFactory,
  DetailContentWrapper,
  ACCORDION_TITLES,
} from '../'

import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { entryPointId } from 'settings/app/constants'
import featureToggles from 'feature-toggles'

const DetailView = (props) => {
  const {
    detailData,
    accordionOrder,
    backPath,
    detailLink,
    detailHeadline,
    bookmarkCount,
    onBookmarkButtonClicked,
    onCompareButtonClicked,
    departmentsOverlayActive,
    onToggleOverlay,
    bookmarkState,
    onToggleBookmark,
    scrollElements,
    disclaimerAnchorId,
    getMaintainableDisclaimer,
    pageType,
    mapLocationDepartments,
    showCostCalculationButton,
  } = props
  const textContext = useContext(TextContext)

  let maintainableTextDisclaimer =
    textContext['general.maintainableTextDisclaimer']
  if (
    pageType.current === entryPointId.CAREHOMES ||
    pageType.current === entryPointId.DAYANDNIGHTCARE
  ) {
    maintainableTextDisclaimer =
      detailData.result?.maintainableTextDisclaimerHint
  }

  return (
    <Styled.DetailView>
      <DetailHeader
        backLink={ backPath }
        detailLink={ detailLink }
        headline={ detailHeadline }
        bookmarkCount={ bookmarkCount }
        onBookmarkButtonClicked={ onBookmarkButtonClicked }
        onCompareButtonClicked={ onCompareButtonClicked }
      />

      <GeneralInformation
        data={ detailData.result }
        type={ pageType.current }
        onToggleBookmark={ () => onToggleBookmark() }
        isBookmarked={ bookmarkState }
        onShowOverlay={ () => {
          onToggleOverlay(true)
        } }
        scrollToElements={ scrollElements }
        showCostCalculationButton={ showCostCalculationButton }
        isTreatmentReason={ detailData.is_treatment_reason }
      />

      {/* map over all detail data wrap content into accordion */}
      {accordionOrder.length > 0 &&
        accordionOrder.map((key, index) => {
          if (!detailData.result.details.hasOwnProperty(key)) return false

          const itemData = detailData.result.details[key]

          // check whether mapped accordion titles exist, if not use title from staticLabels directly
          const accordionHasOverwrite =
            !!ACCORDION_TITLES[pageType.current] &&
            ACCORDION_TITLES[pageType.current].hasOwnProperty(key) &&
            textContext[`${ ACCORDION_TITLES[pageType.current][key] }.title`]

          const label =
            accordionHasOverwrite || textContext[`${ key }.title`] || ''

          let filteredItemData = filterTreatments(itemData)

          // skip filtering for treatments equals zero
          if (key === 'accessibility') {
            filteredItemData = itemData
          }

          if (
            filteredItemData instanceof Array &&
            filteredItemData.length === 0
          ) {
            return null
          }

          // empty quality accordion should be hidden
          if (
            key === 'quality' &&
            !itemData.koro &&
            !itemData.qsr &&
            itemData.qsr?.hasOwnProperty('data') &&
            Array.isArray(itemData.qsr.data) &&
            itemData.qsr.data.length === 0 &&
            !itemData.iqtig &&
            itemData.iqtiq?.hasOwnProperty('data') &&
            Array.isArray(itemData.iqtig.data) &&
            itemData.iqtig.data.length === 0 &&
            !itemData.structuredQualityReport &&
            !itemData.cancerCenters &&
            !itemData.minimumAmountRegulation
          ) {
            return null
          }

          // empty prices accordion should be hidden
          if (
            key === 'prices' &&
            ((pageType.current === entryPointId.CAREHOMES &&
              itemData.hasOwnProperty('shortTermCare') &&
              itemData.shortTermCare.careFocus.length === 0 &&
              itemData.hasOwnProperty('longTermCare') &&
              itemData.longTermCare.careFocus.length === 0) ||
              (pageType.current === entryPointId.DAYANDNIGHTCARE &&
                itemData.hasOwnProperty('dayAndNightCare') &&
                itemData.dayAndNightCare.careFocus.length === 0))
          ) {
            return null
          }

          // empty facts accordion should be hidden
          if (
            key === 'facts' &&
            Array.isArray(itemData) &&
            itemData.length === 0
          ) {
            return false
          }

          if (key === 'branches' && !featureToggles.branches) {
            return null
          }

          return filteredItemData ? (
            <div
              id={ key }
              key={ index }
              data-cy-detail-item={ label }
              className={ 'detail-item-container print-border' }>
              <Accordion
                label={ label }
                tooltip={ itemData.tooltip }
                disclaimerAnchor={ getMaintainableDisclaimer(key) }>
                <ErrorBoundary
                  view={
                    <DetailContentWrapper>
                      <MessageBox isError={ true } showClose={ false } />
                    </DetailContentWrapper>
                  }>
                  <DetailContentFactory
                    detailsKey={ key }
                    entryPoint={ pageType.current }
                    resultData={ detailData.result }
                    itemData={ filteredItemData }
                    isTreatmentReason={ detailData.is_treatment_reason }
                    backLink={ backPath }
                  />
                </ErrorBoundary>
              </Accordion>
            </div>
          ) : null
        })}

      <div className={ 'detail-link-container' }>
        {/* link to BE*/}
        {detailData.result?.editInformationLink?.url && (
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                detailData.result.editInformationLink.url
              ),
            }}
          />
        )}
        <div />
        <PrintButton />
      </div>

      {/* disclaimer anchor text -show only if maintainable accordions are present */}
      {detailData.result?.maintainableTextDisclaimer &&
        detailData.result.maintainableTextDisclaimer.length > 0 && (
          <AnchorText
            id={ disclaimerAnchorId }
            copy={ maintainableTextDisclaimer }
          />
        )}

      {/* overlay for departments (hospitals only)*/}
      {detailData.result.locationDepartments && (
        <Overlay
          id={ 'detail-location-departments' }
          title={ 'Overlay Title' }
          isActive={ departmentsOverlayActive }
          onToggleActive={ onToggleOverlay }
          iconName={ 'icon-l-city' }>
          {/* use link list component => map locationDepartments data */}
          <LinkList
            items={ mapLocationDepartments(
              detailData.result.locationDepartments
            ) }
          />
        </Overlay>
      )}

      {detailData.result.details.readMore &&
        Array.isArray(detailData.result.details.readMore) &&
        detailData.result.details.readMore.length > 0 && (
          <>
            <SubHeading>{textContext['readMoreTeaser.subHeading']}</SubHeading>
            {detailData.result.details.readMore.map((teaser, index) => (
              <ReadMoreTeaser
                key={ index }
                href={ teaser.href }
                content={ teaser.content }
                headline={ teaser.headline }
              />
            ))}
          </>
        )}
    </Styled.DetailView>
  )
}

DetailView.propTypes = {
  detailData: shape({
    result: shape({
      details: shape({
        readMore: arrayOf({
          href: string,
          content: string,
          headline: string,
        }),
      }).isRequired,
      is_treatment_reason: bool,
      locationDepartments: arrayOf(object),
      editInformationLink: shape({
        target: oneOf(['_blank']),
        url: string,
      }),
    }).isRequired,
  }).isRequired,
  accordionOrder: arrayOf(string).isRequired,
  backPath: shape({
    id: number,
    to: string,
  }).isRequired,
  detailLink: shape({
    id: number,
    to: string,
  }),
  detailHeadline: string.isRequired,
  bookmarkCount: oneOfType([number, bool]).isRequired,
  onBookmarkButtonClicked: oneOfType([bool, func]).isRequired,
  onCompareButtonClicked: oneOfType([bool, func]).isRequired,
  departmentsOverlayActive: bool.isRequired,
  onToggleOverlay: func,
  bookmarkState: bool,
  onToggleBookmark: func,
  scrollElements: oneOfType([
    arrayOf(
      shape({
        searchFor: arrayOf(string),
        scrollTo: string,
      })
    ),
    bool,
  ]).isRequired,
  disclaimerAnchorId: string.isRequired,
  getMaintainableDisclaimer: func.isRequired,
  pageType: shape({
    current: string,
  }).isRequired,
  mapLocationDepartments: func.isRequired,
  showCostCalculationButton: bool,
}

export default DetailView
